import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Checkbox,
  Code,
  Divider,
  Heading,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { DeleteIcon, WarningTwoIcon } from "@chakra-ui/icons";
import adminApi from "../../connection/adminApi";

const REGISTRY_LABELS = {
  users: "users",
  stores: "store",
  subscriptions: "subscriptions",
  usageCounters: "usage counters",
  whitelistedDomains: "whitelisted domains",
  whatsappBots: "whatsapp bots",
  botConfigs: "bot configs",
  conversations: "conversations",
  embeddings: "embeddings",
};

/**
 * Wipes a store completely. Written for clearing test accounts, so the dialog
 * shows exactly what is about to go and makes the operator type the username
 * back before the button turns on — there is no undo on the other side.
 */
const PurgeStoreButton = ({ store, size = "sm", iconOnly = false, onPurged }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const navigate = useNavigate();

  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [result, setResult] = useState(null);
  const [options, setOptions] = useState({
    dropDatabase: true,
    deleteStripeCustomer: true,
    deleteUploads: true,
    force: false,
  });

  const loadPreview = useCallback(async () => {
    setLoading(true);

    try {
      const { data } = await adminApi.get(
        `api/admin/stores/${store.id}/purge-preview`
      );
      setPreview(data);
    } catch (error) {
      toast({
        status: "error",
        title: "Could not read what would be deleted",
        description: error?.response?.data?.message,
      });
      onClose();
    } finally {
      setLoading(false);
    }
  }, [store.id, toast, onClose]);

  useEffect(() => {
    if (isOpen) {
      setConfirm("");
      setResult(null);
      loadPreview();
    }
  }, [isOpen, loadPreview]);

  const matches = confirm.trim() === store.username;
  const blocked = preview?.billable && !options.force;

  const purge = async () => {
    setRunning(true);

    try {
      const { data } = await adminApi.delete(`api/admin/stores/${store.id}`, {
        data: { confirm: confirm.trim(), ...options },
      });

      setResult(data);

      if (data.ok) {
        toast({
          status: "success",
          duration: 6000,
          title: `${data.username} purged`,
          description: "Database, registry rows, cache and uploads are gone.",
        });
        // Nothing is left to point at, so either the caller refreshes its
        // list or the store page steps back to it.
        setTimeout(() => (onPurged ? onPurged() : navigate("/admin")), 1500);
      } else {
        toast({
          status: "warning",
          duration: 12000,
          isClosable: true,
          title: "Purged with errors",
          description: "Some steps failed — see the list in the dialog.",
        });
      }
    } catch (error) {
      toast({
        status: "error",
        duration: 12000,
        isClosable: true,
        title: "Nothing was deleted",
        description: error?.response?.data?.message || "The purge was refused.",
      });
    } finally {
      setRunning(false);
    }
  };

  return (
    <>
      {iconOnly ? (
        <Tooltip label={`Purge ${store.username}`} openDelay={400}>
          <Button
            size={size}
            colorScheme="red"
            variant="ghost"
            aria-label={`Purge ${store.username}`}
            onClick={onOpen}
            px={2}
            // Icons size to the font, so this is what makes the glyph bigger.
            fontSize="lg"
          >
            <DeleteIcon />
          </Button>
        </Tooltip>
      ) : (
        <Button
          size={size}
          colorScheme="red"
          variant="outline"
          leftIcon={<WarningTwoIcon />}
          onClick={onOpen}
        >
          Purge store
        </Button>
      )}

      <Modal isOpen={isOpen} onClose={onClose} size="xl" closeOnOverlayClick={!running}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader color="red.600">
            Purge {store.username} permanently
          </ModalHeader>
          <ModalCloseButton isDisabled={running} />

          <ModalBody>
            {loading || !preview ? (
              <Skeleton height="220px" borderRadius="md" />
            ) : result ? (
              <Stack spacing={2}>
                <Alert status={result.ok ? "success" : "warning"} borderRadius="md">
                  <AlertIcon />
                  {result.ok
                    ? `${result.username} is gone.`
                    : "Some steps failed. The rest went through."}
                </Alert>

                {result.steps.map((step) => (
                  <HStack key={step.step} fontSize="sm" align="start">
                    <Badge colorScheme={step.ok ? "green" : "red"}>
                      {step.step}
                    </Badge>
                    <Text color="gray.600">{step.detail}</Text>
                  </HStack>
                ))}
              </Stack>
            ) : (
              <Stack spacing={4}>
                <Alert status="error" borderRadius="md">
                  <AlertIcon />
                  <Box fontSize="sm">
                    This cannot be undone. There is no backup step and no
                    restore path.
                  </Box>
                </Alert>

                {preview.removesUser === false ? (
                  <Alert status="info" borderRadius="md">
                    <AlertIcon />
                    <Box fontSize="sm">
                      This account owns another store, so the login, bots and
                      whitelisted domains stay. Only this store and its own data
                      are removed.
                    </Box>
                  </Alert>
                ) : null}

                {preview.billable ? (
                  <Alert status="warning" borderRadius="md">
                    <AlertIcon />
                    <Box fontSize="sm">
                      This store has a live Stripe subscription (
                      {preview.stripe.status}). Tick “force” below to purge it
                      anyway.
                    </Box>
                  </Alert>
                ) : null}

                <Box>
                  <Heading size="xs" mb={2}>
                    Tenant database
                  </Heading>
                  <Text fontSize="sm">
                    <Code>{preview.databaseName || "none"}</Code>{" "}
                    {preview.databaseExists ? (
                      <Badge colorScheme="red">
                        {preview.databaseSizeMb ?? 0} MB — will be dropped
                      </Badge>
                    ) : (
                      <Badge>not present</Badge>
                    )}
                  </Text>
                </Box>

                <Box>
                  <Heading size="xs" mb={2}>
                    Registry rows
                  </Heading>
                  <SimpleGrid columns={{ base: 2, md: 3 }} spacing={1}>
                    {Object.entries(preview.registry).map(([key, count]) => (
                      <Text key={key} fontSize="sm" color={count ? "gray.800" : "gray.400"}>
                        {REGISTRY_LABELS[key] || key}: <strong>{count}</strong>
                      </Text>
                    ))}
                  </SimpleGrid>
                </Box>

                <Box>
                  <Heading size="xs" mb={2}>
                    Also removed
                  </Heading>
                  <Text fontSize="sm" color="gray.600">
                    Redis cache for <Code>{preview.username}</Code> · Stripe
                    customer{" "}
                    <Code>{preview.stripe.customerId || "none"}</Code> ·{" "}
                    {preview.uploads.exists
                      ? `${preview.uploads.files} uploaded file(s)`
                      : "no uploads"}
                  </Text>
                </Box>

                <Divider />

                <Stack spacing={1}>
                  <Checkbox
                    isChecked={options.dropDatabase}
                    onChange={(event) =>
                      setOptions({ ...options, dropDatabase: event.target.checked })
                    }
                  >
                    Drop the tenant database
                  </Checkbox>
                  <Checkbox
                    isChecked={options.deleteStripeCustomer}
                    onChange={(event) =>
                      setOptions({
                        ...options,
                        deleteStripeCustomer: event.target.checked,
                      })
                    }
                  >
                    Delete the Stripe customer (cancels its subscriptions)
                  </Checkbox>
                  <Checkbox
                    isChecked={options.deleteUploads}
                    onChange={(event) =>
                      setOptions({ ...options, deleteUploads: event.target.checked })
                    }
                  >
                    Delete uploaded files
                  </Checkbox>
                  {preview.billable ? (
                    <Checkbox
                      colorScheme="red"
                      isChecked={options.force}
                      onChange={(event) =>
                        setOptions({ ...options, force: event.target.checked })
                      }
                    >
                      Force — this store is being billed
                    </Checkbox>
                  ) : null}
                </Stack>

                <Box>
                  <Text fontSize="sm" mb={1}>
                    Type <Code>{store.username}</Code> to enable the button:
                  </Text>
                  <Input
                    size="sm"
                    value={confirm}
                    placeholder={store.username}
                    onChange={(event) => setConfirm(event.target.value)}
                    isDisabled={running}
                  />
                </Box>
              </Stack>
            )}
          </ModalBody>

          <ModalFooter gap={2}>
            <Button size="sm" variant="ghost" onClick={onClose} isDisabled={running}>
              {result ? "Close" : "Cancel"}
            </Button>
            {result ? null : (
              <Button
                size="sm"
                colorScheme="red"
                onClick={purge}
                isLoading={running}
                loadingText="Purging"
                isDisabled={!matches || blocked || loading}
              >
                Purge everything
              </Button>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default PurgeStoreButton;
